import { Button, Loading, Modal, notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { FetchService } from '@affine/core/modules/cloud';
import {
  type DialogComponentProps,
  type WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { IntegrationService } from '@affine/core/modules/integration';
import type {
  GitHubBranch,
  GitHubRefMeta,
  GitHubRepository,
} from '@affine/core/modules/integration/type';
import { UrlService } from '@affine/core/modules/url';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  docLinkBaseURLMiddleware,
  MarkdownAdapter,
} from '@blocksuite/affine/shared/adapters';
import { DoneIcon, GithubIcon, WarningIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';

import { RepoPicker } from './repo-picker';
import * as styles from './styles.css';

// Helper to escape special regex characters
function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

type SaveMode = 'commit' | 'pull-request';
type Status = 'select-destination' | 'form' | 'saving' | 'success' | 'error';

export const GitHubSaveDialog = ({
  close,
  docId,
  markdownContent: providedMarkdownContent,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['github-save']>) => {
  const github = useService(IntegrationService).github;
  const urlService = useService(UrlService);
  const fetchService = useService(FetchService);
  const settings = useLiveData(github.settings$);
  const workspaceService = useService(WorkspaceService);
  const workspace = workspaceService.workspace;
  const docsService = useService(DocsService);

  // Get the integration ref
  const ref = github.getDocIntegrationRef(docId);
  const refMeta = ref?.refMeta as GitHubRefMeta | undefined;

  // Get doc title for default filename
  const docRecord = useLiveData(docsService.list.doc$(docId));
  const docTitle = useLiveData(docRecord?.title$) || 'Untitled';

  const [status, setStatus] = useState<Status>(
    refMeta ? 'form' : 'select-destination'
  );
  const [markdownContent, setMarkdownContent] = useState<string | undefined>(
    providedMarkdownContent
  );
  const [saveMode, setSaveMode] = useState<SaveMode>('commit');
  const [commitMessage, setCommitMessage] = useState('Add from yoDEV');
  // The PR title follows the commit message until the user edits it
  const [prTitleOverride, setPrTitle] = useState<string | null>(null);
  const prTitle = prTitleOverride ?? commitMessage;
  const [prBody, setPrBody] = useState('');
  const [showPrBody, setShowPrBody] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  // State for destination selection (new docs)
  const [repos, setRepos] = useState<GitHubRepository[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(
    null
  );
  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [filePath, setFilePath] = useState('');
  const [fileName, setFileName] = useState(
    `${docTitle
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()}.md`
  );
  const [loadingRepos, setLoadingRepos] = useState(false);

  // Export markdown if not provided
  useEffect(() => {
    const exportMarkdown = async () => {
      if (providedMarkdownContent) return;

      const docRecord = docsService.list.doc$(docId).value;
      if (!docRecord) return;

      const doc = workspace.docCollection.getDoc(docId);
      if (!doc) return;

      try {
        const store = doc.getStore();
        const job = store.getTransformer([
          docLinkBaseURLMiddleware(store.workspace.id),
        ]);
        const snapshot = job.docToSnapshot(store);
        if (!snapshot) return;

        const adapter = new MarkdownAdapter(job, store.provider);
        const result = await adapter.fromDocSnapshot({
          snapshot,
          assets: job.assetsManager,
        });

        // Get the doc title and prepend as H1
        // We strip H1 on import to avoid duplication, so we add it back on export
        // But check if content already has an H1 (from old imports before the fix)
        const docTitle = store.meta?.title || 'Untitled';
        const contentHasH1 = /^#\s+/.test(result.file || '');
        const markdownWithTitle = contentHasH1
          ? result.file || ''
          : `# ${docTitle}\n\n${result.file || ''}`;

        console.log(
          '[GitHub Save] Exported markdown, length:',
          markdownWithTitle.length
        );
        setMarkdownContent(markdownWithTitle);
      } catch (err) {
        console.error('[GitHub Save] Failed to export markdown:', err);
      }
    };
    exportMarkdown().catch(console.error);
  }, [docId, docsService, providedMarkdownContent, workspace.docCollection]);

  // Check for conflicts on mount
  useEffect(() => {
    const checkConflicts = async () => {
      if (!refMeta) return;
      const { hasConflict } = await github.checkForConflicts(docId);
      setHasConflict(hasConflict);
    };
    checkConflicts().catch(console.error);
  }, [github, docId, refMeta]);

  // Update commit message based on whether this is a new file or update
  useEffect(() => {
    if (refMeta) {
      setCommitMessage('Update from yoDEV');
    } else {
      setCommitMessage(`Add ${docTitle}`);
    }
  }, [refMeta, docTitle]);

  // Load repositories for new docs
  useEffect(() => {
    if (refMeta) return; // Skip if already linked
    if (!settings?.accessToken) return;

    const loadRepos = async () => {
      setLoadingRepos(true);
      try {
        const repoList = await github.listRepos(1, 100, 'pushed');
        setRepos(repoList);
      } catch (error) {
        notify.error({
          title: 'Failed to load repositories',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setLoadingRepos(false);
      }
    };

    loadRepos().catch(console.error);
  }, [github, settings?.accessToken, refMeta]);

  // Load branches when repo is selected
  useEffect(() => {
    if (!selectedRepo) {
      setBranches([]);
      setSelectedBranch('');
      return;
    }

    const loadBranches = async () => {
      try {
        const branchList = await github.listBranches(
          selectedRepo.owner.login,
          selectedRepo.name,
          1,
          100
        );
        setBranches(branchList);
        // Auto-select default branch
        const defaultBranch = branchList.find(
          b => b.name === selectedRepo.default_branch
        );
        if (defaultBranch) {
          setSelectedBranch(defaultBranch.name);
        } else if (branchList.length > 0) {
          setSelectedBranch(branchList[0].name);
        }
      } catch (error) {
        notify.error({
          title: 'Failed to load branches',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    loadBranches().catch(console.error);
  }, [github, selectedRepo]);

  // Extract blob references from markdown content
  // Returns array of { key, fullRef } where key is the blob hash
  const extractBlobRefs = useCallback(
    (markdown: string): Array<{ key: string; fullRef: string }> => {
      const blobRefs: Array<{ key: string; fullRef: string }> = [];
      const seen = new Set<string>();

      // Pattern for image references: ![...](assets/hashKey.extension)
      const imagePattern = /!\[[^\]]*\]\((assets\/[^)]+)\)/g;
      let match;

      while ((match = imagePattern.exec(markdown)) !== null) {
        const fullRef = match[1]; // e.g., "assets/abc123=.png"
        if (!fullRef.startsWith('http://') && !fullRef.startsWith('https://')) {
          // Extract the key (remove assets/ prefix and extension)
          const withoutPrefix = fullRef.replace(/^assets\//, '');
          const key = withoutPrefix.replace(/\.[a-zA-Z]+$/, '');

          if (key && !seen.has(key)) {
            seen.add(key);
            blobRefs.push({ key, fullRef });
          }
        }
      }

      return blobRefs;
    },
    []
  );

  // Request signed URLs from backend and replace blob refs in markdown
  const transformBlobRefsToSignedUrls = useCallback(
    async (markdown: string): Promise<string> => {
      const blobRefs = extractBlobRefs(markdown);
      if (blobRefs.length === 0) {
        return markdown;
      }

      const workspaceId = workspaceService.workspace.id;
      const blobKeys = blobRefs.map(ref => ref.key);

      console.log(
        `[GitHub Save] Requesting signed URLs for ${blobKeys.length} blobs`
      );

      try {
        // Request signed URLs from backend
        const response = await fetchService.fetch(
          '/api/github/sign-blob-urls',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              workspaceId,
              blobKeys,
              expiresInMinutes: 60, // 1 hour should be enough
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to get signed URLs: ${response.status}`);
        }

        const { signedUrls } = await response.json();

        // Replace blob refs with signed URLs
        let processedMarkdown = markdown;
        for (const ref of blobRefs) {
          const signedUrl = signedUrls[ref.key];
          if (signedUrl) {
            processedMarkdown = processedMarkdown.replace(
              new RegExp(escapeRegex(ref.fullRef), 'g'),
              signedUrl
            );
            console.log(
              `[GitHub Save] Transformed ${ref.fullRef} -> signed URL`
            );
          }
        }

        return processedMarkdown;
      } catch (error) {
        console.error('[GitHub Save] Failed to get signed URLs:', error);
        // Return original markdown if signing fails
        return markdown;
      }
    },
    [extractBlobRefs, fetchService, workspaceService]
  );

  const handleSave = useAsyncCallback(async () => {
    if (!settings?.accessToken || !markdownContent) return;

    setStatus('saving');
    setError(null);

    try {
      // Transform blob refs to signed URLs for external access
      const processedMarkdown =
        await transformBlobRefsToSignedUrls(markdownContent);
      console.log('[GitHub Save] Saving markdown with signed image URLs');

      if (saveMode === 'commit') {
        const result = await github.saveToGitHub(
          docId,
          processedMarkdown,
          commitMessage
        );
        setResultUrl(result.commitUrl);
        notify.success({
          title: 'Saved to GitHub',
          message: 'Your changes have been committed.',
        });
      } else {
        const result = await github.saveToGitHubAsPR(
          docId,
          processedMarkdown,
          commitMessage,
          prTitle,
          prBody || undefined
        );
        setResultUrl(result.prUrl);
        notify.success({
          title: 'Pull request created',
          message: `PR #${result.prNumber} has been created.`,
        });
      }
      setStatus('success');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save to GitHub';
      // Check for 409 conflict error
      if (
        errorMessage.includes('409') ||
        errorMessage.includes('does not match')
      ) {
        setError(
          'The file has been modified on GitHub since your last sync. ' +
            'Please close this dialog, click the GitHub badge in the header, ' +
            'and select "Pull latest from GitHub" to update your local copy first.'
        );
      } else {
        setError(errorMessage);
      }
      setStatus('error');
    }
  }, [
    github,
    docId,
    markdownContent,
    commitMessage,
    saveMode,
    prTitle,
    prBody,
    settings?.accessToken,
    transformBlobRefsToSignedUrls,
  ]);

  const handleCancel = useCallback(() => {
    close(undefined);
  }, [close]);

  const handleComplete = useCallback(() => {
    close({
      success: true,
      commitUrl: saveMode === 'commit' ? resultUrl || undefined : undefined,
      prUrl: saveMode === 'pull-request' ? resultUrl || undefined : undefined,
    });
  }, [close, saveMode, resultUrl]);

  const handleViewResult = useCallback(() => {
    if (resultUrl) {
      urlService.openPopupWindow(resultUrl);
    }
  }, [urlService, resultUrl]);

  // Handle creating a new file on GitHub (for unlinked docs)
  const handleCreateNewFile = useAsyncCallback(async () => {
    if (
      !settings?.accessToken ||
      !markdownContent ||
      !selectedRepo ||
      !selectedBranch ||
      !fileName
    )
      return;

    setStatus('saving');
    setError(null);

    try {
      // Transform blob refs to signed URLs for external access
      const processedMarkdown =
        await transformBlobRefsToSignedUrls(markdownContent);
      console.log('[GitHub Save] Creating new file with signed image URLs');

      // Build the full path
      const fullPath = filePath
        ? `${filePath.replace(/^\/|\/$/g, '')}/${fileName}`
        : fileName;

      let sha: string;
      if (saveMode === 'commit') {
        // Create the file on GitHub
        const result = await github.createOrUpdateFile(
          selectedRepo.owner.login,
          selectedRepo.name,
          fullPath,
          processedMarkdown,
          commitMessage,
          undefined, // No SHA for new file
          selectedBranch
        );
        sha = result.sha;
        setResultUrl(result.commitUrl);
        notify.success({
          title: 'Saved to GitHub',
          message: 'Your document has been created on GitHub.',
        });
      } else {
        const result = await github.createFileAsPR(
          docId,
          selectedRepo.owner.login,
          selectedRepo.name,
          fullPath,
          processedMarkdown,
          commitMessage,
          selectedBranch,
          prTitle,
          prBody || undefined
        );
        sha = result.sha;
        setResultUrl(result.prUrl);
        notify.success({
          title: 'Pull request created',
          message: `PR #${result.prNumber} has been created.`,
        });
      }

      // Link the doc to the file on the base branch. For a PR, the blob sha
      // matches the base branch once the PR is merged unchanged.
      await github.setDocIntegrationRef(
        docId,
        selectedRepo.owner.login,
        selectedRepo.name,
        fullPath,
        selectedBranch,
        sha
      );

      setStatus('success');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save to GitHub';
      setError(errorMessage);
      setStatus('error');
    }
  }, [
    github,
    docId,
    markdownContent,
    commitMessage,
    saveMode,
    prTitle,
    prBody,
    selectedRepo,
    selectedBranch,
    filePath,
    fileName,
    settings?.accessToken,
    transformBlobRefsToSignedUrls,
  ]);

  const idPrefix = useId();
  const fieldId = (name: string) => `${idPrefix}-${name}`;

  const isNewFile = !refMeta;
  const targetBranch = refMeta?.branch ?? selectedBranch;
  const folder = filePath.replace(/^\/+|\/+$/g, '');
  const fullPath = folder ? `${folder}/${fileName}` : fileName;

  const canSubmit =
    !!markdownContent &&
    !!commitMessage.trim() &&
    (saveMode === 'commit' || !!prTitle.trim()) &&
    (!isNewFile || (!!selectedRepo && !!selectedBranch && !!fileName.trim()));

  const submit = isNewFile ? handleCreateNewFile : handleSave;

  // Enter in a single-line field submits; the repo search handles its own Enter
  const handleFormKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key === 'Enter' &&
        !e.nativeEvent.isComposing &&
        e.target instanceof HTMLInputElement &&
        canSubmit
      ) {
        e.preventDefault();
        submit();
      }
    },
    [canSubmit, submit]
  );

  const renderSaveMode = () => (
    <div className={styles.field}>
      <span className={styles.label} id={fieldId('mode')}>
        Save as
      </span>
      <div
        className={styles.segmented}
        role="radiogroup"
        aria-labelledby={fieldId('mode')}
      >
        <button
          type="button"
          role="radio"
          aria-checked={saveMode === 'commit'}
          className={styles.segment}
          onClick={() => setSaveMode('commit')}
        >
          Commit
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={saveMode === 'pull-request'}
          className={styles.segment}
          onClick={() => setSaveMode('pull-request')}
        >
          Pull request
        </button>
      </div>
      <span className={styles.hint}>
        {saveMode === 'commit' ? (
          <>
            Commits straight to{' '}
            <span className={styles.code}>{targetBranch || 'the branch'}</span>.
          </>
        ) : (
          <>
            Opens a pull request into{' '}
            <span className={styles.code}>{targetBranch || 'the branch'}</span>{' '}
            from a new branch.
          </>
        )}
      </span>
    </div>
  );

  const renderMessageFields = (disabled = false) => (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={fieldId('message')}>
          Commit message
        </label>
        <input
          id={fieldId('message')}
          type="text"
          className={styles.input}
          value={commitMessage}
          onChange={e => setCommitMessage(e.target.value)}
          placeholder="Describe your changes"
          disabled={disabled}
        />
      </div>
      {saveMode === 'pull-request' && (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={fieldId('pr-title')}>
            Pull request title
          </label>
          <input
            id={fieldId('pr-title')}
            type="text"
            className={styles.input}
            value={prTitle}
            onChange={e => setPrTitle(e.target.value)}
            placeholder="Pull request title"
            disabled={disabled}
          />
          {showPrBody ? (
            <textarea
              aria-label="Pull request description"
              className={styles.textarea}
              value={prBody}
              onChange={e => setPrBody(e.target.value)}
              placeholder="Describe the changes in this pull request"
              autoFocus
              rows={3}
            />
          ) : (
            <button
              type="button"
              className={styles.textButton}
              onClick={() => setShowPrBody(true)}
              disabled={disabled}
            >
              + Add description
            </button>
          )}
        </div>
      )}
    </>
  );

  const renderDestinationForm = () => (
    <div className={styles.columns}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={fieldId('repo')}>
          Repository
        </label>
        <RepoPicker
          id={fieldId('repo')}
          repos={repos}
          loading={loadingRepos}
          selected={selectedRepo}
          onSelect={setSelectedRepo}
        />
      </div>

      <div className={styles.stack}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={fieldId('branch')}>
            Branch
          </label>
          <select
            id={fieldId('branch')}
            className={styles.select}
            value={selectedBranch}
            onChange={e => setSelectedBranch(e.target.value)}
            disabled={!selectedRepo || branches.length === 0}
          >
            {!selectedRepo && <option value="">Choose a repository</option>}
            {branches.map(branch => (
              <option key={branch.name} value={branch.name}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={fieldId('folder')}>
            Location
          </label>
          <div className={styles.locationRow}>
            <input
              id={fieldId('folder')}
              type="text"
              className={styles.input}
              value={filePath}
              onChange={e => setFilePath(e.target.value)}
              placeholder="folder (optional)"
              aria-label="Folder"
            />
            <span className={styles.locationSlash}>/</span>
            <input
              type="text"
              className={styles.input}
              value={fileName}
              onChange={e => setFileName(e.target.value)}
              placeholder="my-document.md"
              aria-label="Filename"
            />
          </div>
        </div>

        {renderSaveMode()}
        {renderMessageFields()}
      </div>
    </div>
  );

  const renderLinkedForm = () =>
    refMeta && (
      <div className={styles.stack}>
        <div className={styles.destination}>
          <GithubIcon className={styles.headerIcon} />
          <div className={styles.destinationText}>
            <span className={styles.destinationRepo}>
              {refMeta.owner}/{refMeta.repo}
            </span>
            <span className={styles.destinationPath}>
              {refMeta.branch} · {refMeta.path}
            </span>
          </div>
        </div>

        {hasConflict && (
          <div className={styles.conflictWarning} role="alert">
            <WarningIcon className={styles.warningIcon} />
            <div>
              <div className={styles.warningTitle}>
                File has changed on GitHub
              </div>
              <div className={styles.warningDescription}>
                It was modified since you last synced. Saving will overwrite the
                remote changes.
              </div>
            </div>
          </div>
        )}

        {renderSaveMode()}
        {renderMessageFields()}
      </div>
    );

  const renderStatus = () => {
    switch (status) {
      case 'saving':
        return (
          <div className={styles.status} role="status">
            <Loading size={28} />
            <div className={styles.statusTitle}>
              {saveMode === 'commit'
                ? 'Committing changes…'
                : 'Creating pull request…'}
            </div>
            <p className={styles.statusMessage}>
              Hang on while your document is saved to GitHub.
            </p>
          </div>
        );
      case 'success':
        return (
          <div className={styles.status}>
            <div className={styles.statusIcon} data-tone="success">
              <DoneIcon />
            </div>
            <div className={styles.statusTitle}>
              {saveMode === 'commit'
                ? 'Changes committed'
                : 'Pull request created'}
            </div>
            <p className={styles.statusMessage}>
              Your document is saved to GitHub.
            </p>
            {resultUrl && (
              <a
                className={styles.resultLink}
                href={resultUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => {
                  e.preventDefault();
                  handleViewResult();
                }}
              >
                View on GitHub ↗
              </a>
            )}
          </div>
        );
      case 'error':
        return (
          <div className={styles.status} role="alert">
            <div className={styles.statusIcon} data-tone="error">
              <WarningIcon />
            </div>
            <div className={styles.statusTitle}>Couldn't save</div>
            <p className={styles.statusMessage}>
              {error || 'Something went wrong while saving to GitHub.'}
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const isEditing = status === 'select-destination' || status === 'form';

  let footer: ReactNode;
  if (isEditing) {
    footer = (
      <>
        <span
          className={styles.footerSummary}
          title={isNewFile ? fullPath : undefined}
        >
          {isNewFile && selectedRepo
            ? `${selectedRepo.full_name} · ${selectedBranch} · ${fullPath}`
            : null}
        </span>
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!canSubmit}
            loading={!markdownContent}
          >
            {saveMode === 'pull-request'
              ? 'Create pull request'
              : isNewFile
                ? 'Save to GitHub'
                : 'Commit'}
          </Button>
        </div>
      </>
    );
  } else if (status === 'success') {
    footer = (
      <div className={styles.footerActions}>
        <Button variant="primary" onClick={handleComplete}>
          Done
        </Button>
      </div>
    );
  } else if (status === 'error') {
    footer = (
      <div className={styles.footerActions}>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        {/* An unlinked doc has no 'form' screen; it retries from its destination */}
        <Button
          variant="primary"
          onClick={() => setStatus(refMeta ? 'form' : 'select-destination')}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <Modal
      open
      onOpenChange={(open: boolean) => {
        if (!open) close(undefined);
      }}
      width={status === 'select-destination' ? 760 : 560}
      title={
        <>
          <GithubIcon className={styles.headerIcon} />
          Save to GitHub
          <span className={styles.headerDoc}>· {docTitle}</span>
        </>
      }
      headerClassName={styles.header}
      contentOptions={{ className: styles.modalContent }}
      withoutCloseButton={status === 'saving'}
      persistent={status === 'saving'}
    >
      <div className={styles.body} onKeyDown={handleFormKeyDown}>
        {status === 'select-destination'
          ? renderDestinationForm()
          : status === 'form'
            ? renderLinkedForm()
            : renderStatus()}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </Modal>
  );
};
